import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { OmnibarEntry } from './omnibar-interfaces';
import { useFeatureToggles } from '../../feature-toggles';
import { catalogCorrectionsToggle } from './utils/catalog-corrections';

export interface OmnibarServiceProviderCitizen {
  citizenId: string;
  fullName?: string;
  serviceProviderUnitName?: string;
  entries: OmnibarEntry[];
}

export interface OmnibarAuthorityCitizen {
  citizenId: string;
  fullName?: string;
  opticLabel?: string;
  isEmploymentOptic: boolean;
  isAnonymous: boolean;
}

interface IOmnibarContext {
  showOmnibar: boolean;
  toggleOmnibar: (forcedState?: boolean) => void;
  serviceProviderCitizen?: OmnibarServiceProviderCitizen;
  setServiceProviderCitizen: (citizen: OmnibarServiceProviderCitizen | undefined) => void;
  authorityCitizen?: OmnibarAuthorityCitizen;
  setAuthorityCitizen: (citizen: OmnibarAuthorityCitizen | undefined) => void;
}

export const OmnibarContext = React.createContext<IOmnibarContext | undefined>(undefined);

export function OmnibarProvider({ children }: { children?: React.ReactNode }) {
  const [showOmnibar, setShowOmnibar] = useState(false);
  const [serviceProviderCitizen, setServiceProviderCitizen] = useState<OmnibarServiceProviderCitizen>();
  const [authorityCitizen, setAuthorityCitizen] = useState<OmnibarAuthorityCitizen>();
  const { isFeatureEnabled } = useFeatureToggles();
  const preventShortcutSpace = isFeatureEnabled(catalogCorrectionsToggle);

  const toggleOmnibar = useCallback(
    (forcedState?: boolean) => {
      const newState = forcedState !== undefined ? forcedState : !showOmnibar;
      setShowOmnibar(newState);
    },
    [showOmnibar]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.code === 'Space') {
        // The input gets focus before the keystroke lands, so the space would start the search.
        if (preventShortcutSpace) event.preventDefault();
        toggleOmnibar();
      }
      if (event.code === 'Escape') {
        toggleOmnibar(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleOmnibar, preventShortcutSpace]);

  const value = useMemo(
    () => ({ showOmnibar, toggleOmnibar, serviceProviderCitizen, setServiceProviderCitizen, authorityCitizen, setAuthorityCitizen }),
    [showOmnibar, toggleOmnibar, serviceProviderCitizen, authorityCitizen]
  );

  return <OmnibarContext.Provider value={value}>{children}</OmnibarContext.Provider>;
}

export function useOmnibar() {
  const context = React.useContext(OmnibarContext);
  if (context === undefined) {
    throw new Error('useOmnibar must be used within a OmnibarProvider');
  }
  return context;
}
